import { getCurrentUser, invalidateCurrentUser } from '@/hooks/query/useQueryUser';
import { getUser } from '@/lib/utils';
import type { BindingCodeSource } from '@/models/analytics/features/chronicle';
import { listHardwareDevices, updateUser } from '@/pages/api/scribeAPI';
import { setChronicleActiveUser } from '@/tauri/chronicle/active-user';
import { logger } from '@/utils/logger/logger';

export type { BindingCodeSource };

const BINDING_CODE_LENGTH = 16;
const BINDING_CODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

export interface BindingCodeResult {
  code: string;
  isNew: boolean;
  source: BindingCodeSource;
}

export function generateBindingCode(): string {
  const array = new Uint8Array(BINDING_CODE_LENGTH);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) =>
    BINDING_CODE_CHARS.charAt(byte % BINDING_CODE_CHARS.length)
  ).join('');
}

/**
 * Return the user's binding code: cached user → GET /user/ → generate new.
 * Does NOT persist — only a device-confirmed code reaches the server, via
 * `syncBindingCodeToServer` after a successful handshake.
 */
export async function getOrGenerateBindingCode(): Promise<BindingCodeResult> {
  let bindingCode = getCurrentUser()?.hardware_device_binding_code;
  if (!bindingCode) {
    const { data: user } = await getUser();
    bindingCode = user?.hardware_device_binding_code;
  }

  if (bindingCode) {
    return { code: bindingCode, isNew: false, source: 'server' };
  }

  return { code: generateBindingCode(), isNew: true, source: 'generated' };
}

/**
 * Pushes the signed-in account to Rust and returns it, or `null` when nobody is
 * signed in. Every connect path awaits this first: the actor drops identity
 * writes it cannot attribute, so a connect that runs ahead of the push stores
 * neither the confirmed binding code nor the reconnect target.
 *
 * Resolves from the user cache, falling back to the API — on a cold launch the
 * cache has not hydrated, and pushing that `null` reads to Rust as an explicit
 * sign-out, which tears down the connection this is about to establish.
 */
export async function pushSignedInChronicleUser(): Promise<string | null> {
  const cached = getCurrentUser()?.user_ref_id;
  const userRefId = cached ?? (await getUser()).data?.user_ref_id ?? null;
  if (!userRefId) return null;
  await setChronicleActiveUser(userRefId);
  return userRefId;
}

export interface SyncBindingCodeOptions {
  /**
   * Serial (or advertised name) of the device that confirmed the code. Checked
   * against the signed-in user's linked devices before anything is written.
   */
  connectedSerial?: string | null;
  /**
   * The user just paired this device by hand, so it is theirs by construction —
   * and `GET /hardware-device/` may not list it yet.
   */
  justPaired?: boolean;
}

/**
 * Whether the device that confirmed the code belongs to the signed-in user.
 *
 * Refuses when ownership cannot be established. Refusing is not free — startup
 * re-seeds the local store from the server every launch, so a server value this
 * gate keeps rejecting stays in place. It is still the cheaper side: writing
 * another account's code costs the user their own device, because their profile
 * would then hold a key their Remote rejects.
 */
async function isConfirmedByOwnDevice({
  connectedSerial,
  justPaired,
}: SyncBindingCodeOptions): Promise<boolean> {
  if (justPaired) return true;
  if (!connectedSerial) return false;

  const { data } = await listHardwareDevices();
  return (
    data?.some(
      (device) =>
        device.serial_number === connectedSerial || device.device_name === connectedSerial
    ) ?? false
  );
}

/**
 * APP-4359: After a successful BLE handshake (MCU&SK&OK), ensure the
 * device-confirmed binding code is PATCHed to the server so all machines converge.
 *
 * Compares the device-confirmed code against the cached server value
 * and PATCHes only when they differ, and only when the device that confirmed it
 * is one of the signed-in user's.
 *
 * Mirrors iOS `syncConfirmedBindingCodeToServerIfNeeded`.
 * Best-effort — failures are logged but do not break the connection.
 */
export async function syncBindingCodeToServer(
  confirmedCode: string,
  options: SyncBindingCodeOptions = {}
): Promise<void> {
  try {
    const serverCode = getCurrentUser()?.hardware_device_binding_code;
    if (serverCode === confirmedCode) return;

    if (!(await isConfirmedByOwnDevice(options))) {
      logger.warn({
        msg: 'Refusing to sync a binding code confirmed by a device this account does not own',
      });
      return;
    }

    await updateUser({ hardware_device_binding_code: confirmedCode });
    await invalidateCurrentUser();
    logger.info({
      msg: 'Binding code synced to server after successful handshake',
    });
  } catch (error) {
    logger.warn({
      msg: 'Failed to sync binding code to server after handshake',
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
